import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 60000;

function MaintenanceAlert() {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(null);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  // The poll runs in an interval, so it reads the current state through refs
  // (a closure would only ever see the first render's values).
  const modeRef = useRef(false);
  const warningRef = useRef(false);
  useEffect(() => { modeRef.current = maintenanceMode; }, [maintenanceMode]);
  useEffect(() => { warningRef.current = showWarning; }, [showWarning]);

  useEffect(() => {
    checkMaintenanceStatus();
    // Once a minute, and only while the tab is visible; coming back to the
    // tab checks at once. (Was every 3 s in every open tab.)
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') checkMaintenanceStatus();
    }, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') checkMaintenanceStatus(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisible); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && !maintenanceMode) {
      // Countdown finished before maintenance - do logout
      handleLogout();
    } else if (countdown === 0 && maintenanceMode) {
      // Maintenance countdown finished - redirect to login
      window.location.href = '/login';
    }
  }, [countdown, maintenanceMode]);

  const checkMaintenanceStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/maintenance/status`);
      const { enabled } = response.data || {};
      if (enabled && !modeRef.current && !warningRef.current) {
        // maintenance just started: warn, then count down
        setShowWarning(true);
        setCountdown(10);
      } else if (!enabled && (modeRef.current || warningRef.current)) {
        setMaintenanceMode(false);
        setShowWarning(false);
        setCountdown(null);
      }
    } catch (error) {
      // network hiccup: try again on the next poll
    }
  };

  const handleLogout = () => {
    // Save user data before logout
    const hasUnsavedWork = checkUnsavedWork();
    if (hasUnsavedWork) {
      alert(t('shell.maintenance.saveChangesAlert'));
    }

    // Clear auth and redirect
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setMaintenanceMode(true);
    setShowWarning(false);
    
    // Start 5 minute countdown (300 seconds)
    setCountdown(300);
  };

  const checkUnsavedWork = () => {
    // Check if there are any unsaved forms
    const forms = document.querySelectorAll('form');
    for (let form of forms) {
      const inputs = form.querySelectorAll('input, textarea, select');
      for (let input of inputs) {
        if (input.value && input.value !== input.defaultValue) {
          return true;
        }
      }
    }
    return false;
  };

  // Format countdown to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Maintenance Mode Full Screen
  if (maintenanceMode) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-blue-900 to-blue-700 flex items-center justify-center z-[9999]">
        <div className="text-center text-white p-8 max-w-2xl">
          <div className="mb-8">
            <div className="inline-block">
              <svg className="animate-spin h-20 w-20 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          </div>
          <h1 className="text-5xl font-bold mb-6">🔧 {t('shell.maintenance.title')}</h1>
          <p className="text-2xl mb-3">{t('shell.maintenance.subtitle')}</p>
          <p className="text-xl opacity-90 mb-8">{t('shell.maintenance.addingFeatures')}</p>
          
          {countdown !== null && countdown > 0 && (
            <div className="bg-white dark:bg-surface-900 bg-opacity-20 rounded-2xl p-8 mb-6 backdrop-blur-sm">
              <p className="text-lg mb-3 opacity-90">{t('shell.maintenance.estimatedTime')}</p>
              <div className="text-7xl font-bold tracking-wider mb-2">
                {formatTime(countdown)}
              </div>
              <p className="text-sm opacity-75">{t('shell.maintenance.minutes')}</p>
            </div>
          )}
          
          <div className="mt-8 space-y-3 text-base opacity-90">
            <p>✨ {t('shell.maintenance.workingOnBetterExperience')}</p>
            <p>🚀 {t('shell.maintenance.backSoon')}</p>
            <p className="text-sm mt-4 opacity-75">
              {t('shell.maintenance.redirectNotice')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Warning Banner
  if (showWarning && countdown !== null) {
    return (
      <div className="fixed top-0 left-0 right-0 bg-red-600 text-white py-4 px-6 shadow-lg z-[9998] animate-pulse">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="text-3xl">⚠️</div>
            <div>
              <h3 className="font-bold text-lg">{t('shell.maintenance.warningTitle')}</h3>
              <p className="text-sm">{t('shell.maintenance.warningBody')}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold">{countdown}</div>
            <div className="text-sm">{t('shell.maintenance.seconds', { count: countdown })}</div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default MaintenanceAlert;
